import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { 
  XMarkIcon, 
  MapPinIcon, 
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { placesAPI } from '../../services/api';

interface LocationData {
  address: string;
  lat: number;
  lng: number;
}

interface MapPinDropProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: LocationData) => void;
  initialLocation?: LocationData;
}

const containerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 6.5244,
  lng: 3.3792
};

const MapPinDrop: React.FC<MapPinDropProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  initialLocation
}) => {
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : defaultCenter
  );
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : defaultCenter
  );
  const [address, setAddress] = useState(initialLocation?.address || '');
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['geometry', 'places']
  });

  useEffect(() => {
    if (isOpen && !initialLocation) {
      getCurrentLocation();
    }
  }, [isOpen]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPos = { lat: latitude, lng: longitude };
        setMarkerPosition(newPos);
        setMapCenter(newPos);
        reverseGeocode(latitude, longitude);
        setGpsLoading(false);
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }

    setLoading(true);
    setAddress('');

    geocodeTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await placesAPI.reverseGeocode(lat, lng);
        setAddress(result.formatted_address);
      } catch (error) {
        console.error('Reverse geocode error:', error);
        setAddress(`Location (${lat.toFixed(6)}, ${lng.toFixed(6)})`);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerPosition({ lat, lng });
      setShowPredictions(false);
      reverseGeocode(lat, lng);
    }
  };

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerPosition({ lat, lng });
      reverseGeocode(lat, lng);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await placesAPI.autocomplete(query);
        if (data.predictions) {
          setPredictions(data.predictions);
          setShowPredictions(true);
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300);
  };

  const handlePredictionSelect = async (prediction: any) => {
    try {
      const data = await placesAPI.details(prediction.place_id);
      const newPos = { lat: data.lat, lng: data.lng };
      setMarkerPosition(newPos);
      setMapCenter(newPos);
      setAddress(data.formatted_address);
      setSearchQuery('');
      setPredictions([]);
      setShowPredictions(false);
      if (mapRef.current) {
        mapRef.current.panTo(newPos);
      }
    } catch (error) {
      console.error('Place details error:', error);
    }
  };

  const handleConfirm = () => {
    onLocationSelect({
      address: address,
      lat: markerPosition.lat,
      lng: markerPosition.lng
    });
    onClose();
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  if (!isOpen) return null;

  if (loadError) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl max-w-md text-center">
          <p className="text-red-500 mb-4">Failed to load Google Maps</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-lg">Close</button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl max-w-md text-center">
          <ArrowPathIcon className="h-8 w-8 text-sky-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white w-full h-full md:w-[90%] md:h-[90%] md:max-w-4xl md:rounded-xl overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MapPinIcon className="h-6 w-6" />
            <h2 className="text-lg font-semibold">Pin Your Location</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-3 bg-gray-50 border-b relative">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search for an area or landmark..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          
          {showPredictions && predictions.length > 0 && (
            <div className="absolute left-3 right-3 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
              {predictions.map((prediction, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePredictionSelect(prediction)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start space-x-2">
                    <MapPinIcon className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{prediction.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 relative">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={15}
            onClick={handleMapClick}
            onLoad={onMapLoad}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              gestureHandling: 'greedy'
            }}
          >
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
              animation={google.maps.Animation.DROP}
            />
          </GoogleMap>

          <button
            onClick={getCurrentLocation}
            disabled={gpsLoading}
            className="absolute top-3 right-3 bg-white p-3 rounded-full shadow-lg hover:bg-gray-50 transition-colors z-[1000]"
            title="Use my current location"
          >
            {gpsLoading ? (
              <ArrowPathIcon className="h-5 w-5 text-sky-500 animate-spin" />
            ) : (
              <svg className="h-5 w-5 text-sky-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
              </svg>
            )}
          </button>

          <div className="absolute bottom-0 left-0 right-0 text-center text-xs text-gray-600 bg-white/80 py-1 z-[1000]">
            Tap on map or drag the pin to set location
          </div>
        </div>

        <div className="p-4 bg-white border-t">
          <div className="mb-3 bg-gray-50 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <MapPinIcon className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <ArrowPathIcon className="h-4 w-4 text-gray-400 animate-spin" />
                    <span className="text-sm text-gray-500">Getting address...</span>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-900 truncate">{address || 'No address selected'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleConfirm}
            disabled={!address || loading}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all"
          >
            <CheckIcon className="h-5 w-5" />
            <span>Use This Location</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapPinDrop;
